import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

export const DEFAULT_SIGN_IN_REDIRECT = '/appointments';

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    NSIGHT_WEB_PORT: z.string().trim().min(1).default('3000'),
    AVATAR_READ_WRITE_TOKEN: z.string().trim().min(1),
    LOGOS_READ_WRITE_TOKEN: z.string().trim().min(1),
    CLERK_SECRET_KEY: z.string().trim().min(1),
    CLERK_WEBHOOK_SECRET: z.string().trim().min(1),
    ABLY_API_KEY: z.string().trim().min(1),
    EHR_BRIDGE_TOKEN: z.string().optional(),
    CRON_SECRET: z.string().length(16),
    DATADOG_APP_ID: z.string(),
    DATADOG_CLIENT_TOKEN: z.string(),
    ENV: z
      .enum(['dev', 'staging', 'demo', 'production', 'production-2'])
      .default('dev'),
    S3_BUCKET: z.string(),
    SYMPTOM_S3_BUCKET: z.string(),
    AVATAR_S3_BUCKET: z.string(),
    BRANDING_S3_BUCKET: z.string(),
    NSIGHT_URL: z.string().url().default('http://localhost:3000'),
    RSA_ENCRYPTION_PUBLIC_KEY: z.string(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_AGORA_ID: z.string().trim().min(1),
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: z.string(),
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: z.string(),
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string().trim().min(1),
    NEXT_PUBLIC_INACTIVE_TIMEOUT_MS: z.string().optional().default('1200000'),
    NEXT_PUBLIC_TRPC_GATEWAY_URL: z
      .string()
      .url()
      .default('http://localhost:5555'),
    NEXT_PUBLIC_TRANSCRIPTION_WEBSOCKET_URL: z
      .string()
      .url()
      .default('http://localhost:8282/'),
    EHR_BRIDGE_TOKEN: z.string().optional(),
    NEXT_PUBLIC_DATADOG_APPLICATION_ID: z.string().uuid().optional(),
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_DATADOG_SITE: z.string().default('datadoghq.com'),
    NEXT_PUBLIC_DATADOG_SERVICE: z.string().optional(),
    NEXT_PUBLIC_DATADOG_ENV: z.string().optional(),
    NEXT_PUBLIC_APP_VERSION: z.string().min(1).optional(),
    NEXT_PUBLIC_DATAGOG_API_KEY: z.string().optional(),
    NEXT_PUBLIC_ENCRYPTION_KEY: z.string().default('r8Z6Xqb73QX0ON/88MZPKQ=='),
    NEXT_PUBLIC_USERFLOW_TOKEN: z.string(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NEXT_PUBLIC_CLERK_SIGN_IN_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_IN_URL,
    NEXT_PUBLIC_CLERK_SIGN_UP_URL: process.env.NEXT_PUBLIC_CLERK_SIGN_UP_URL,
    NEXT_PUBLIC_TRPC_GATEWAY_URL: process.env.NEXT_PUBLIC_TRPC_GATEWAY_URL,
    NSIGHT_WEB_PORT: process.env.NSIGHT_WEB_PORT,
    AVATAR_READ_WRITE_TOKEN: process.env.AVATAR_READ_WRITE_TOKEN,
    LOGOS_READ_WRITE_TOKEN: process.env.LOGOS_READ_WRITE_TOKEN,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    CLERK_SECRET_KEY: process.env.CLERK_SECRET_KEY,
    CLERK_WEBHOOK_SECRET: process.env.CLERK_WEBHOOK_SECRET,
    NEXT_PUBLIC_TRANSCRIPTION_WEBSOCKET_URL:
      process.env.NEXT_PUBLIC_TRANSCRIPTION_WEBSOCKET_URL,
    NSIGHT_URL: process.env.NSIGHT_URL,
    ABLY_API_KEY: process.env.ABLY_API_KEY,
    EHR_BRIDGE_TOKEN: process.env.EHR_BRIDGE_TOKEN,
    CRON_SECRET: process.env.CRON_SECRET,
    DATADOG_APP_ID: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    ENV: process.env.ENV,
    DATADOG_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    NEXT_PUBLIC_DATADOG_APPLICATION_ID:
      process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    NEXT_PUBLIC_DATADOG_CLIENT_TOKEN:
      process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    NEXT_PUBLIC_DATAGOG_API_KEY: process.env.NEXT_PUBLIC_DATAGOG_API_KEY,
    NEXT_PUBLIC_DATADOG_SITE: process.env.NEXT_PUBLIC_DATADOG_SITE,
    NEXT_PUBLIC_DATADOG_SERVICE: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
    NEXT_PUBLIC_DATADOG_ENV: process.env.NEXT_PUBLIC_DATADOG_ENV,
    NEXT_PUBLIC_AGORA_ID: process.env.NEXT_PUBLIC_AGORA_ID,
    NEXT_PUBLIC_USERFLOW_TOKEN: process.env.NEXT_PUBLIC_USERFLOW_TOKEN,
    S3_BUCKET: process.env.S3_BUCKET,
    SYMPTOM_S3_BUCKET: process.env.SYMPTOM_S3_BUCKET,
    AVATAR_S3_BUCKET: process.env.AVATAR_S3_BUCKET,
    BRANDING_S3_BUCKET: process.env.BRANDING_S3_BUCKET,
    NEXT_PUBLIC_ENCRYPTION_KEY: process.env.NEXT_PUBLIC_ENCRYPTION_KEY,
    RSA_ENCRYPTION_PUBLIC_KEY: process.env.RSA_ENCRYPTION_PUBLIC_KEY,
  },
  /**
   * Makes it so that empty strings are treated as undefined.
   * `SOME_VAR: z.string()` and `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
